import { useEffect, useRef, useState } from "react";
import "./VenuesPanel.css";
import { RefreshCw } from "lucide-react";

import {
  geocodeCity,
  getImportJob,
  getImportJobs,
  startVenueImport,
  type GeocodeHit,
  type ImportJob,
} from "../../../Services/Admin/Venues";

/**
 * Startar och följer en omseedning av venues från OpenStreetMap.
 *
 * Importen kan ta minuter och ger över tusen träffar, så den körs som ett
 * bakgrundsjobb på servern. Här startar man den och pollar status.
 *
 * Allt nytt hamnar i needs_review och syns inte på kartan förrän någon
 * godkänt det. Ett ställe som satts till hidden eller active för hand rörs
 * aldrig av en import.
 */
const POLL_MS = 2000;

export default function ImportPanel({ onFinished }: { onFinished: () => void }) {
  const [job, setJob] = useState<ImportJob | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cityQuery, setCityQuery] = useState("");
  const [hits, setHits] = useState<GeocodeHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<GeocodeHit | null>(null);

  const finishedRef = useRef(false);

  // Plocka upp ett jobb som redan kör, t.ex. efter en omladdning.
  useEffect(() => {
    getImportJobs()
      .then((jobs) => {
        const active = jobs.find((j) => j.status === "queued" || j.status === "running");
        setJob(active ?? jobs[0] ?? null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!job || (job.status !== "queued" && job.status !== "running")) return;

    const timer = window.setInterval(async () => {
      try {
        const next = await getImportJob(job.id);
        setJob(next);

        if (next.status === "done" && !finishedRef.current) {
          finishedRef.current = true;
          onFinished();
        }
      } catch {
        // En tappad poll är inte värd att visa fel för — nästa försöker igen.
      }
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [job, onFinished]);

  const searchCity = async () => {
    if (cityQuery.trim().length < 2) return;

    setSearching(true);
    setError(null);
    setPicked(null);

    try {
      const items = await geocodeCity(cityQuery);
      setHits(items);
      if (items.length === 0) setError(`No place found for "${cityQuery}".`);
    } catch (e: any) {
      setError(e?.message ?? "Could not look up that place");
    } finally {
      setSearching(false);
    }
  };

  const start = async () => {
    setStarting(true);
    setError(null);
    finishedRef.current = false;

    try {
      // Utan vald stad körs Göteborg, som är serverns default.
      const res = await startVenueImport(
        picked ? { bbox: picked.bbox, city: picked.city } : undefined
      );
      setJob(await getImportJob(res.jobId));
    } catch (e: any) {
      setError(
        e?.message === "import_already_running"
          ? "An import is already running."
          : (e?.message ?? "Could not start the import")
      );
    } finally {
      setStarting(false);
    }
  };

  const isRunning = job?.status === "queued" || job?.status === "running";
  const done = job ? job.created + job.updated + job.skipped : 0;

  return (
    <div className="import-panel">
      <div className="import-panel-head">
        <div>
          <strong>Re-seed venues</strong>
          <span className="import-panel-hint">
            Fetches bars and clubs from OpenStreetMap. New places land in
            needs_review and stay off the map until approved.
          </span>
        </div>

        <button
          type="button"
          className="admin-btn--primary"
          disabled={starting || isRunning}
          onClick={start}
        >
          <RefreshCw size={14} strokeWidth={2.2} />
          {isRunning ? "Running…" : starting ? "Starting…" : "Run import"}
        </button>
      </div>

      <div className="import-panel-city">
        <input
          placeholder="City to import (leave empty for Gothenburg)"
          value={cityQuery}
          onChange={(e) => setCityQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void searchCity();
            }
          }}
          disabled={isRunning}
        />
        <button
          type="button"
          disabled={searching || isRunning || cityQuery.trim().length < 2}
          onClick={searchCity}
        >
          {searching ? "Searching…" : "Find"}
        </button>
      </div>

      {hits.length > 0 && (
        <div className="import-panel-hits">
          {hits.map((hit) => (
            <button
              key={hit.name}
              type="button"
              className={`import-panel-hit${picked?.name === hit.name ? " is-picked" : ""}`}
              disabled={hit.tooLarge || isRunning}
              title={hit.tooLarge ? "Area too large for Overpass — pick a smaller place" : hit.name}
              onClick={() => setPicked(hit)}
            >
              <strong>{hit.city}</strong>
              <span>{hit.name}</span>
              {hit.tooLarge && <em>too large</em>}
            </button>
          ))}
        </div>
      )}

      {picked && (
        <p className="import-panel-picked">
          Importing <strong>{picked.city}</strong>
        </p>
      )}

      {error && <p className="admin-error">{error}</p>}

      {job && (
        <div className="import-panel-status">
          <span className={`venue-pill ${job.status}`}>{job.status}</span>

          {job.total > 0 && (
            <span>
              {done}/{job.total} processed
            </span>
          )}

          <span>
            {job.created} new · {job.updated} updated · {job.skipped} skipped
          </span>

          {job.message && (
            <span className={job.status === "failed" ? "import-panel-error" : ""}>
              {job.message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
